import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { GlobalQuery } from '../state/global';
import { SessionQuery } from '../state/session/session.query';
import { resolveStartingRoute } from '../state/global/starting-page.utils';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

/**
 * Gates the file library routes on the `enableFileLibrary` toggle.
 *
 * Ignores the starting page on purpose: a site can start users on the legacy dashboard and still
 * let them reach the library from navigation.
 */
export const FileLibraryEnabledGuard: CanActivateFn = (route, state) => {
	const globalQuery = inject(GlobalQuery);
	const router = inject(Router);

	return globalQuery.select('settings').pipe(
		filter(s => s !== undefined && s !== null),
		take(1),
		map(s => {
			if (!s?.fileLibrarySettings?.enableFileLibrary) {
				return router.parseUrl('/');
			}
			return true;
		})
	);
};

/**
 * Sends users hitting `/` to their configured starting page.
 *
 * Shares `resolveStartingPage()` with `FileLibraryEnabledGuard` so the two can't disagree and
 * redirect into each other. A server-confirmed guest stays on `/`, because every library route
 * carries `NoGuestGuard`, which sends guests here.
 *
 * Profile hydration is deliberately not awaited: on a cold load settings are loaded only after
 * `/user/me` settles, and pages that never start `/user/me` (`/share/*`, `/login`, ...) must not hang `/`.
 */
export const HomeRouteGuard: CanActivateFn = (route, state) => {
	const globalQuery = inject(GlobalQuery);
	const sessionQuery = inject(SessionQuery);
	const router = inject(Router);

	return globalQuery.select('settings').pipe(
		filter(s => s !== undefined && s !== null),
		take(1),
		withLatestFrom(sessionQuery.notificationSettingsAccess$),
		map(([s, access]) => {
			const guest = access.profileHydrationStatus === 'ready' && !access.available;
			const target = resolveStartingRoute(s, { guest });
			// '/' is this route — load it rather than redirect to self.
			return target === '/' ? true : router.parseUrl(target);
		})
	);
};
